import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Container,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  HStack,
  Button,
  Badge,
  Select,
  Card,
  CardBody,
  useBreakpointValue,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import { FiTrash2, FiEye } from 'react-icons/fi'
import { getAdminQnaList, deleteAdminQna } from '../../services/apiService'
import dayjs from 'dayjs'

const adminNavItems = [
  { label: '회원 관리', path: '/admin/user' },
  { label: '커뮤니티 관리', path: '/admin/community' },
  { label: 'Q&A 관리', path: '/admin/qna' },
  { label: '공지사항 관리', path: '/admin/notice' },
  { label: '자산 관리', path: '/admin/asset' },
  { label: '경매사례 관리', path: '/admin/auction-case' },
]

interface QnA {
  id: number
  userId: number
  username: string
  title: string
  question: string
  answer: string | null
  answeredBy: number | null
  answeredByUsername: string | null
  answeredAt: string | null
  status: string
  isPrivate: number
  createdAt: string
  updatedAt: string | null
}

const QnA = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const [qnas, setQnas] = useState<QnA[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const cancelRef = React.useRef<HTMLButtonElement>(null)

  const fetchQnas = async () => {
    setLoading(true)
    try {
      const { data: res } = await getAdminQnaList(page, limit)
      if (res.statusCode === 200) {
        setQnas(res.data.qnas)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('질문 목록을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchQnas()
  }, [page, limit])

  const handleDeleteClick = (id: number) => {
    setDeletingId(id)
    onOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!deletingId) return

    try {
      const { data: res } = await deleteAdminQna(deletingId)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
        setDeletingId(null)
        fetchQnas()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <DefaultLayout>
      <AdminNav items={adminNavItems} />
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg">Q&A 관리</Heading>

          <Box
            p={6}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
          >
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    총 {total}개의 질문
                  </Text>
                  <Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value))
                      setPage(1)
                    }}
                    width="120px"
                    size="sm"
                  >
                    <option value={10}>10개씩</option>
                    <option value={20}>20개씩</option>
                    <option value={50}>50개씩</option>
                  </Select>
                </HStack>

                {isMobile ? (
                  <VStack spacing={4} align="stretch">
                    {qnas.length === 0 ? (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">등록된 질문이 없습니다.</Text>
                      </Box>
                    ) : (
                      qnas.map((qna) => (
                        <Card key={qna.id} size="sm">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <HStack justify="space-between">
                                <HStack spacing={2}>
                                  <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                                    {qna.title}
                                  </Text>
                                  {qna.isPrivate === 1 && (
                                    <Badge colorScheme="purple" size="sm">비밀글</Badge>
                                  )}
                                  {qna.status === 'ANSWERED' ? (
                                    <Badge colorScheme="green" size="sm">답변완료</Badge>
                                  ) : (
                                    <Badge colorScheme="gray" size="sm">대기중</Badge>
                                  )}
                                </HStack>
                                <HStack spacing={2}>
                                  <IconButton
                                    aria-label={t('상세보기')}
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/qna/${qna.id}`)}
                                  />
                                  <IconButton
                                    aria-label={t('삭제')}
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(qna.id)}
                                  />
                                </HStack>
                              </HStack>
                              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                {qna.question}
                              </Text>
                              <HStack justify="space-between" fontSize="xs" color="gray.500">
                                <Text>{qna.username}</Text>
                                <Text>{dayjs(qna.createdAt).format('YYYY-MM-DD')}</Text>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </VStack>
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>ID</Th>
                          <Th>제목</Th>
                          <Th>작성자</Th>
                          <Th>상태</Th>
                          <Th>비밀글</Th>
                          <Th>작성일시</Th>
                          <Th>관리</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {qnas.length === 0 ? (
                          <Tr>
                            <Td colSpan={7} textAlign="center" py={8}>
                              <Text color="gray.500">등록된 질문이 없습니다.</Text>
                            </Td>
                          </Tr>
                        ) : (
                          qnas.map((qna) => (
                            <Tr key={qna.id}>
                              <Td>{qna.id}</Td>
                              <Td>
                                <HStack spacing={2}>
                                  <Text
                                    fontWeight="medium"
                                    cursor="pointer"
                                    _hover={{ color: 'brand.600' }}
                                    onClick={() => navigate(`/qna/${qna.id}`)}
                                    noOfLines={1}
                                    maxW="300px"
                                  >
                                    {qna.title}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td>{qna.username}</Td>
                              <Td>
                                {qna.status === 'ANSWERED' ? (
                                  <Badge colorScheme="green">답변완료</Badge>
                                ) : (
                                  <Badge colorScheme="gray">대기중</Badge>
                                )}
                              </Td>
                              <Td>
                                {qna.isPrivate === 1 ? (
                                  <Badge colorScheme="purple">비밀글</Badge>
                                ) : (
                                  <Text color="gray.400">-</Text>
                                )}
                              </Td>
                              <Td>
                                {dayjs(qna.createdAt).format('YYYY-MM-DD HH:mm')}
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <IconButton
                                    aria-label={t('상세보기')}
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/qna/${qna.id}`)}
                                  />
                                  <IconButton
                                    aria-label={t('삭제')}
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(qna.id)}
                                  />
                                </HStack>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {totalPages > 0 && (
                  <HStack justify="center" spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      isDisabled={page === 1}
                    >
                      {t('이전')}
                    </Button>
                    <Text fontSize="sm">
                      {page} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      isDisabled={page >= totalPages}
                    >
                      {t('다음')}
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default QnA

